/**
 * V11.6 — Assistant IA multi-disciplines pour organiser un palmarès.
 *
 * Workflow :
 *  1. L'utilisateur saisit un texte libre (ex: "Champion V6 500m 2025 Faaa Nui, 1:52.34").
 *  2. L'IA structure en entrées Achievement discipline-aware (preview).
 *  3. L'utilisateur peut cocher/décocher, corriger un champ clé, marquer "titre majeur".
 *  4. Commit via POST /api/palmares/{athlete_id}/bulk-commit.
 *
 * Design :
 *  - Discipline-aware : le prompt IA + les colonnes affichées s'adaptent à la famille.
 *  - Preview obligatoire avant commit (aucun risque de pollution silencieuse).
 *  - Confidence badge par ligne (structuré / à confirmer).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { showError } from "@/lib/errors";
import { Sparkles, Wand2, Loader2, CheckCircle2, AlertCircle, Trophy, Users, Star } from "lucide-react";

const FAMILY_LABELS = {
  combat: "Combat",
  team: "Collectif",
  timed: "Chronométré",
  vaa: "Va'a",
  strength: "Force",
  score_judge: "Score/Jugement",
  racket: "Raquette",
  shooting: "Tir",
  generic: "Générique",
};

/**
 * Colonnes affichées dans la preview selon la famille.
 * Chaque colonne : { key, label, width?: string }
 */
const PREVIEW_COLUMNS_BY_FAMILY = {
  combat: [
    { key: "title", label: "Titre" },
    { key: "year", label: "Année", width: "w-16" },
    { key: "weight", label: "Poids" },
    { key: "medal", label: "Médaille" },
    { key: "rank", label: "Rang" },
  ],
  team: [
    { key: "title", label: "Titre" },
    { key: "year", label: "Année", width: "w-16" },
    { key: "team_name", label: "Équipe" },
    { key: "medal", label: "Médaille" },
    { key: "rank", label: "Rang" },
  ],
  timed: [
    { key: "title", label: "Titre" },
    { key: "year", label: "Année", width: "w-16" },
    { key: "distance", label: "Distance" },
    { key: "time_performance", label: "Temps" },
    { key: "rank", label: "Rang" },
  ],
  vaa: [
    { key: "title", label: "Titre" },
    { key: "year", label: "Année", width: "w-16" },
    { key: "crew_format", label: "Format" },
    { key: "distance", label: "Distance" },
    { key: "team_name", label: "Équipage" },
    { key: "rank", label: "Rang" },
  ],
  strength: [
    { key: "title", label: "Titre" },
    { key: "year", label: "Année", width: "w-16" },
    { key: "weight", label: "Cat. poids" },
    { key: "rank", label: "Rang" },
  ],
  score_judge: [
    { key: "title", label: "Titre" },
    { key: "year", label: "Année", width: "w-16" },
    { key: "rank", label: "Rang" },
    { key: "medal", label: "Médaille" },
  ],
  racket: [
    { key: "title", label: "Titre" },
    { key: "year", label: "Année", width: "w-16" },
    { key: "rank", label: "Rang" },
    { key: "medal", label: "Médaille" },
  ],
  shooting: [
    { key: "title", label: "Titre" },
    { key: "year", label: "Année", width: "w-16" },
    { key: "rank", label: "Rang" },
    { key: "medal", label: "Médaille" },
  ],
  generic: [
    { key: "title", label: "Titre" },
    { key: "year", label: "Année", width: "w-16" },
    { key: "medal", label: "Médaille" },
    { key: "rank", label: "Rang" },
  ],
};

export function PalmaresAIOrganizer({
  athleteId,
  coachId,
  entityType = "athlete", // "athlete" | "coach"
  discipline,
  onCommitted,
  disciplines = [],
  label = "Organiser avec l'IA",
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [selectedDiscipline, setSelectedDiscipline] = useState(discipline || "");
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null); // { entries, family, status, family_spec, raw, error }
  const [checked, setChecked] = useState({}); // { [idx]: bool }
  const [edits, setEdits] = useState({}); // { [idx]: {field:value} }

  const family = preview?.family || "generic";
  const columns = PREVIEW_COLUMNS_BY_FAMILY[family] || PREVIEW_COLUMNS_BY_FAMILY.generic;

  useEffect(() => {
    if (!open) {
      // Reset state on close
      setText("");
      setPreview(null);
      setChecked({});
      setEdits({});
      setSelectedDiscipline(discipline || "");
    }
  }, [open, discipline]);

  const runAI = async () => {
    if (!text.trim()) {
      toast.error("Saisissez d'abord un texte de palmarès.");
      return;
    }
    setParsing(true);
    setPreview(null);
    try {
      const { data } = await api.post("/palmares/organize-ai", {
        text,
        discipline_hint: selectedDiscipline || undefined,
        athlete_id: entityType === "athlete" ? athleteId : undefined,
      });
      setPreview(data);
      // Tout coché par défaut si structuré
      const nextChecked = {};
      (data.entries || []).forEach((_, i) => { nextChecked[i] = true; });
      setChecked(nextChecked);
      setEdits({});
      if (!data.entries || data.entries.length === 0) {
        toast.warning("L'IA n'a rien trouvé à structurer. Vérifiez votre texte.");
      } else {
        toast.success(`${data.entries.length} résultat(s) structuré(s) — vérifiez avant validation.`);
      }
    } catch (e) {
      showError(e, { prefix: "IA palmarès :" });
    } finally {
      setParsing(false);
    }
  };

  const toggleCheck = (idx) => setChecked((c) => ({ ...c, [idx]: !c[idx] }));

  const patchEntry = (idx, patch) => {
    setEdits((cur) => ({ ...cur, [idx]: { ...(cur[idx] || {}), ...patch } }));
  };

  const finalEntries = useMemo(() => {
    if (!preview?.entries) return [];
    return preview.entries.map((e, i) => ({ ...e, ...(edits[i] || {}) }));
  }, [preview, edits]);

  const selectedCount = Object.values(checked).filter(Boolean).length;

  const commit = async () => {
    const entries = finalEntries.filter((_, i) => checked[i]);
    if (entries.length === 0) {
      toast.error("Aucun résultat sélectionné.");
      return;
    }
    setCommitting(true);
    try {
      const commitEndpoint = entityType === "coach"
        ? `/palmares/coaches/${coachId}/bulk-commit`
        : `/palmares/${athleteId}/bulk-commit`;
      const { data } = await api.post(commitEndpoint, {
        entries,
        raw_text: preview?.raw || text,
      });
      toast.success(`${data.added_count} résultat(s) ajouté(s) au palmarès.`);
      onCommitted?.(data.athlete || data.coach);
      setOpen(false);
    } catch (e) {
      showError(e, { prefix: "Commit palmarès :" });
    } finally {
      setCommitting(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="palmares-ai-open"
        className="gap-1.5 border-[hsl(var(--aito-lagoon))]/40 text-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon))]/10"
      >
        <Sparkles className="h-4 w-4" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="palmares-ai-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
              Organiser mon palmarès avec l'IA
            </DialogTitle>
            <DialogDescription>
              Colle un texte libre décrivant tes résultats. L'IA les structure et te propose une preview.
              <strong> Rien n'est ajouté</strong> tant que tu n'as pas validé.
            </DialogDescription>
          </DialogHeader>

          {/* Étape 1 : discipline + saisie */}
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Discipline (indice IA)</label>
              <select
                data-testid="palmares-ai-discipline"
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-background"
                value={selectedDiscipline}
                onChange={(e) => setSelectedDiscipline(e.target.value)}
                disabled={parsing}
              >
                <option value="">— (auto-détection)</option>
                {disciplines.map((d) => (
                  <option key={d.slug} value={d.slug}>{d.name}</option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Aide l'IA à comprendre le contexte (Va'a, Basket, Natation…) et à extraire les bons champs.
              </p>
            </div>
            <div>
              <label className="text-xs font-medium">Texte à structurer</label>
              <Textarea
                data-testid="palmares-ai-text"
                rows={6}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Ex : Champion de Polynésie V6 500m 2025 avec Faaa Nui, 1er en 1:52.34.&#10;3e Championnat Océanie 2024 catégorie senior."
                disabled={parsing}
                maxLength={8000}
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Astuce : tu peux mettre plusieurs résultats séparés par un retour à la ligne, un ';' ou un tiret.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={runAI}
                disabled={parsing || !text.trim()}
                className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
                data-testid="palmares-ai-run"
              >
                {parsing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
                {parsing ? "Analyse en cours…" : "Analyser avec l'IA"}
              </Button>
            </div>
          </div>

          {/* Étape 2 : preview */}
          {preview && preview.entries && preview.entries.length > 0 && (
            <Card className="p-4 space-y-3" data-testid="palmares-ai-preview">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary" className="gap-1">
                  {preview.status === "STRUCTURÉ"
                    ? <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                    : <AlertCircle className="h-3 w-3 text-amber-600" />}
                  {preview.status}
                </Badge>
                <Badge variant="outline">Famille : {FAMILY_LABELS[preview.family] || preview.family}</Badge>
                <Badge variant="outline">{preview.entries.length} résultat(s)</Badge>
                <div className="ml-auto text-xs text-muted-foreground">
                  {selectedCount} / {preview.entries.length} sélectionné(s)
                </div>
              </div>

              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs" data-testid="palmares-ai-preview-table">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-2 py-2 text-left w-10"></th>
                      {columns.map((c) => (
                        <th key={c.key} className={`px-2 py-2 text-left ${c.width || ""}`}>{c.label}</th>
                      ))}
                      <th className="px-2 py-2 text-center w-16">Titre majeur</th>
                      <th className="px-2 py-2 text-center w-16">Collectif</th>
                      <th className="px-2 py-2 text-left w-24">Confiance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finalEntries.map((e, i) => (
                      <tr key={i} className={`border-t ${checked[i] ? "" : "opacity-40"}`} data-testid={`palmares-ai-row-${i}`}>
                        <td className="px-2 py-1.5">
                          <Checkbox
                            checked={!!checked[i]}
                            onCheckedChange={() => toggleCheck(i)}
                            data-testid={`palmares-ai-row-check-${i}`}
                          />
                        </td>
                        {columns.map((c) => (
                          <td key={c.key} className="px-2 py-1.5">
                            <Input
                              value={e[c.key] ?? ""}
                              onChange={(ev) => patchEntry(i, { [c.key]: ev.target.value })}
                              className="h-7 text-xs px-2"
                              data-testid={`palmares-ai-field-${c.key}-${i}`}
                            />
                          </td>
                        ))}
                        <td className="px-2 py-1.5 text-center">
                          <Checkbox
                            checked={!!e.is_major_title}
                            onCheckedChange={(v) => patchEntry(i, { is_major_title: !!v })}
                            data-testid={`palmares-ai-major-${i}`}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <Checkbox
                            checked={!!e.is_collective}
                            onCheckedChange={(v) => patchEntry(i, { is_collective: !!v })}
                            data-testid={`palmares-ai-collective-${i}`}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          {e.confidence === "structuré" ? (
                            <Badge variant="secondary" className="text-[9px] gap-0.5">
                              <CheckCircle2 className="h-2.5 w-2.5 text-emerald-600" />
                              Structuré
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[9px] gap-0.5 border-amber-400">
                              <AlertCircle className="h-2.5 w-2.5 text-amber-600" />
                              À confirmer
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-[11px] text-muted-foreground">
                Corrige directement les champs si besoin, coche <Star className="inline h-3 w-3 mx-0.5" /> pour marquer un titre majeur,
                puis clique <strong>Ajouter au palmarès</strong>.
              </p>
            </Card>
          )}

          {preview && (!preview.entries || preview.entries.length === 0) && (
            <Card className="p-4 border-amber-300 bg-amber-50 dark:bg-amber-950/20">
              <div className="flex items-start gap-2 text-sm">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium">Aucun résultat structuré.</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {preview.error === "ia_indisponible" ? "Service IA non configuré." : "Le texte est trop court ou ambigu. Précise l'année et le nom de la compétition, puis réessaie."}
                  </p>
                </div>
              </div>
            </Card>
          )}

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="palmares-ai-cancel">Annuler</Button>
            {preview?.entries?.length > 0 && (
              <Button
                onClick={commit}
                disabled={committing || selectedCount === 0}
                className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
                data-testid="palmares-ai-commit"
              >
                {committing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trophy className="h-4 w-4 mr-1.5" />}
                Ajouter {selectedCount} résultat{selectedCount > 1 ? "s" : ""} au palmarès
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PalmaresAIOrganizer;
